import { useContext, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { Context } from "../../../../context";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../../containers/layout/dashboard";
import { SMSAlertWrapper } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { BaseTable } from "../../../../components/table";
import { SearchIcon } from "../../../../assets";
import { BaseConfirmationModal } from "../../../../components/modals/confirmation";
import { retrieveAllSMSAlertService } from "../../../../utils/apis/configuration/smsalert/retrieveAllSMSAlert";
import { deleteSMSAlertService } from "../../../../utils/apis/configuration/smsalert/deleteSMSAlert";
import { retrieveInstitutionService } from "../../../../utils/apis/institution/retrieveInstitution";
import { useQuery } from "@tanstack/react-query";

export const SMSAlert = () => {
	const headers = ["MFI Name", "Sender ID", "Action"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(Context);

	const [filter, setFilter] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedSMSAlertId, setSelectedSMSAlertId] = useState<string>("");
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: filteredSMSAlert, refetch: refetchFilteredSMSAlert } = useQuery(
		{
			queryKey: ["smsalerts", TOKEN, filter],
			queryFn: async () => {
				const smsalerts = await retrieveAllSMSAlertService(filter, TOKEN);
				const transformedSMSAlerts = await Promise.all(
					smsalerts?.map(async (smsalert: any) => {
						const institution = await retrieveInstitutionService(
							TOKEN,
							smsalert.institution
						);
						return {
							...smsalert,
							institution: institution.name,
						};
					})
				);
				return transformedSMSAlerts;
			},
			enabled: !!TOKEN,
		}
	);

	const handleNavigateToAddSMSAlertForm = () => {
		return navigate(`/configuration/smsalert/addsmsalert`);
	};

	const handleFilterChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = e.target.value;
		setFilter(value);
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleCancelClick = () => {
		// reset error to default
		setError(null);
		return setIsConfirmationModalOpen(false);
	};

	const handleCallToActionClick = async (
		e:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
		action?: string
	) => {
		if (action === "edit") {
			return navigate(`/configuration/smsalert/editsmsalert/${id}`);
		}
		if (action === "delete") {
			setSelectedSMSAlertId(id);
			return setIsConfirmationModalOpen(true);
		}
	};

	const handleDeleteSMSAlert = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await deleteSMSAlertService(id, TOKEN);
			if (response.status === "success") {
				setIsLoading(false);
				setIsConfirmationModalOpen(false);
				refetchFilteredSMSAlert();
			} else {
				setIsLoading(false);
				setError("SMS Alert deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`SMS Alert deletion failed. ${error.message}`);
			console.error("SMS Alert deletion failed:", error);
		}
	};

	return (
		<Layout id="SMS-alert">
			<SMSAlertWrapper>
				<BaseConfirmationModal
					error={error}
					isLoading={isLoading}
					open={isConfirmationModalOpen}
					className="delete-sms-alert-confirmation-modal"
					body="Once deleted, you will not be able to recover this information!"
					handleClose={handleConfirmationModalPersist}
					handleConfirm={(e) => handleDeleteSMSAlert(e, selectedSMSAlertId)}
					handleCancel={handleCancelClick}
				/>
				<Stack
					gap={{
						mobile: "calc(var(--flex-gap)/2)",
						miniTablet: "var(--flex-gap)",
					}}
					alignItems={{ miniTablet: "center" }}
					justifyContent={"space-between"}
					direction={{ miniTablet: "row" }}
					overflow={"hidden"}
				>
					<Box>
						<Typography
							variant="h1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={24}
							lineHeight={"normal"}
							color={"#373737"}
						>
							Setup SMS Alert
						</Typography>
					</Box>
					<Box>
						<BaseButton
							variant="contained"
							radius="7px"
							fontSize={16}
							fontWeight={800}
							bgcolor="#791377"
							startIcon={<AddIcon />}
							padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleNavigateToAddSMSAlertForm}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Add
							</Typography>
						</BaseButton>
					</Box>
				</Stack>
				<Box
					marginBlock={"calc(var(--basic-margin)/2) calc(var(--basic-margin))"}
				>
					<form>
						<BaseFieldSet>
							<BaseLabel colour="#505050" fontWeight={500} fontSize="15px">
								Search
							</BaseLabel>
							<BaseInput
								name="filter"
								colour="#666666"
								bgcolor="#FFFFFF"
								fontWeight={400}
								value={filter}
								placeholder="Enter Sender ID"
								onChange={(e) => handleFilterChange(e)}
								startAdornment={
									<Box component={"div"} className="search-icon-box">
										<SearchIcon />
									</Box>
								}
							/>
						</BaseFieldSet>
					</form>
				</Box>
				<BaseTable
					headers={headers}
					rows={filteredSMSAlert as Record<any, any>[]}
					location="all-sms-alerts"
					handleItemClick={handleCallToActionClick}
				/>
			</SMSAlertWrapper>
		</Layout>
	);
};
